import React, { useCallback, useEffect, useRef } from 'react';
import { Device } from 'mediasoup-client';
import { useAuth } from '../../contexts/AuthContext';
import { useSoftphone } from '../../contexts/SoftphoneContext';

const API_BASE = '/api/admin';

const createApiCall = (tokenRef) => async (path, opts = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      Authorization: `Bearer ${tokenRef.current}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  });
  const json = await res.json();
  if (!res.ok || json.success === false) {
    throw new Error(json.message || 'Request failed');
  }
  return json.data ?? json;
};

export default function SupervisorMonitorSfu() {
  const { token, user } = useAuth();
  const {
    monitoringSession,
    setSoftphoneFunctions,
    setMuted,
    setInCall,
    setHasActiveRtp,
    clearMonitoringSession,
    makeCall,
    hangup,
    toggleMute,
    connect,
    disconnect,
    answer,
    reject
  } = useSoftphone();

  const tokenRef = useRef(token);
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const producerRef = useRef(null);
  const audioContainerRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const audioElementsRef = useRef(new Map());
  const consumedRef = useRef(new Set());
  const monitorModeRef = useRef('whisper');
  const sessionActiveRef = useRef(false);
  const functionsSnapshotRef = useRef(null);
  const micEnabledRef = useRef(false);
  const pollTimerRef = useRef(null);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const apiCall = useCallback(createApiCall(tokenRef), []);

  const cleanup = useCallback(() => {
    sessionActiveRef.current = false;
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (producerRef.current) {
      try { producerRef.current.track?.stop(); } catch {}
      try { producerRef.current.close(); } catch {}
    }
    try { sendTransportRef.current?.close(); } catch {}
    try { recvTransportRef.current?.close(); } catch {}
    sendTransportRef.current = null;
    recvTransportRef.current = null;
    producerRef.current = null;
    consumedRef.current = new Set();
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    if (audioElementsRef.current.size) {
      for (const { audio, stream } of audioElementsRef.current.values()) {
        try { audio.pause(); } catch {}
        try { audio.srcObject = null; } catch {}
        try { audio.remove(); } catch {}
        try { stream?.getTracks?.().forEach((track) => track.stop()); } catch {}
      }
      audioElementsRef.current.clear();
    }
    micEnabledRef.current = false;
    setMuted(true);
    setHasActiveRtp(false);
    setInCall(false);
  }, [setMuted, setHasActiveRtp, setInCall]);

  const consumeProducer = useCallback(async (callId, producerId, rtpCapabilities) => {
    if (consumedRef.current.has(producerId)) return;
    if (producerRef.current && producerRef.current.id === producerId) return;
    const recvTransport = recvTransportRef.current;
    if (!recvTransport) return;

    const result = await apiCall(`/sfu/rooms/${callId}/consume`, {
      method: 'POST',
      body: JSON.stringify({
        transportId: recvTransport.id,
        producerId,
        rtpCapabilities
      })
    });

    const consumer = await recvTransport.consume({
      id: result.id,
      producerId: result.producerId,
      kind: result.kind,
      rtpParameters: result.rtpParameters
    });

    consumedRef.current.add(producerId);
    if (!audioContainerRef.current) return;
    const stream = new MediaStream();
    stream.addTrack(consumer.track);
    const audio = document.createElement('audio');
    audio.autoplay = true;
    audio.playsInline = true;
    audio.srcObject = stream;
    audioContainerRef.current.appendChild(audio);
    audioElementsRef.current.set(producerId, { audio, stream });
    await audio.play().catch(() => {});
  }, [apiCall]);

  const ensureProducer = useCallback(async (callId, mode) => {
    if (producerRef.current || !sendTransportRef.current) return;
    const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
    const track = localStream.getAudioTracks()[0];
    const appData = {
      role: 'supervisor',
      broadcastToAsterisk: mode === 'conference'
    };
    producerRef.current = await sendTransportRef.current.produce({ track, appData });
    micEnabledRef.current = true;
    setMuted(false);
  }, [setMuted]);

  const toggleMonitorMute = useCallback(async () => {
    if (!sessionActiveRef.current) return;
    const mode = monitorModeRef.current;
    if (!producerRef.current) {
      await ensureProducer(sessionActiveRef.current, mode);
      return;
    }
    const producer = producerRef.current;
    if (producer.paused) {
      producer.resume();
      micEnabledRef.current = true;
      setMuted(false);
    } else {
      producer.pause();
      micEnabledRef.current = false;
      setMuted(true);
    }
  }, [ensureProducer, setMuted]);

  const stopMonitor = useCallback(async () => {
    cleanup();
    clearMonitoringSession();
  }, [cleanup, clearMonitoringSession]);

  const startMonitor = useCallback(async () => {
    if (!monitoringSession || !tokenRef.current) return;
    const agentId = monitoringSession.agentId;
    if (!agentId) return;

    const snapshot = await apiCall(`/softphone/active-call/${encodeURIComponent(agentId)}`);
    if (!snapshot?.active || !snapshot.callId) {
      throw new Error('No hay una llamada activa para monitorear.');
    }

    const callId = snapshot.callId;
    const mode = monitoringSession.mode === 'conference' ? 'conference' : 'whisper';
    monitorModeRef.current = mode;
    sessionActiveRef.current = callId;

    const rtpCapabilities = await apiCall(`/sfu/rooms/${callId}/rtp-capabilities`);
    const device = new Device();
    await device.load({ routerRtpCapabilities: rtpCapabilities });
    deviceRef.current = device;

    const sendInfo = await apiCall(`/sfu/rooms/${callId}/transport`, { method: 'POST' });
    const recvInfo = await apiCall(`/sfu/rooms/${callId}/transport`, { method: 'POST' });

    const sendTransport = device.createSendTransport(sendInfo);
    const recvTransport = device.createRecvTransport(recvInfo);

    sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await apiCall(`/sfu/rooms/${callId}/transport/${sendTransport.id}/connect`, {
          method: 'POST',
          body: JSON.stringify({ dtlsParameters })
        });
        callback();
      } catch (err) {
        errback(err);
      }
    });

    recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
      try {
        await apiCall(`/sfu/rooms/${callId}/transport/${recvTransport.id}/connect`, {
          method: 'POST',
          body: JSON.stringify({ dtlsParameters })
        });
        callback();
      } catch (err) {
        errback(err);
      }
    });

    sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
      try {
        const result = await apiCall(`/sfu/rooms/${callId}/produce`, {
          method: 'POST',
          body: JSON.stringify({ transportId: sendTransport.id, kind, rtpParameters, appData })
        });
        callback({ id: result.id });
      } catch (err) {
        errback(err);
      }
    });

    sendTransportRef.current = sendTransport;
    recvTransportRef.current = recvTransport;

    const producers = await apiCall(`/sfu/rooms/${callId}/producers`);
    for (const producer of producers) {
      await consumeProducer(callId, producer.id, device.rtpCapabilities);
    }

    // Poll for new producers for a short window
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    pollTimerRef.current = setInterval(async () => {
      if (!sessionActiveRef.current) return;
      try {
        const list = await apiCall(`/sfu/rooms/${callId}/producers`);
        for (const producer of list) {
          await consumeProducer(callId, producer.id, device.rtpCapabilities);
        }
      } catch {}
    }, 2000);

    setInCall(true);
    setHasActiveRtp(true);

    // Start muted by default (listen). User can press "Hablar".
    setMuted(true);

    if (mode === 'conference') {
      // allow talk, but keep muted until user clicks
    }
  }, [monitoringSession, apiCall, consumeProducer, setInCall, setHasActiveRtp, setMuted]);

  useEffect(() => {
    if (!monitoringSession) {
      if (sessionActiveRef.current) {
        cleanup();
      }
      if (functionsSnapshotRef.current) {
        setSoftphoneFunctions(functionsSnapshotRef.current);
        functionsSnapshotRef.current = null;
      }
      return;
    }

    if (!functionsSnapshotRef.current) {
      functionsSnapshotRef.current = { makeCall, hangup, toggleMute, connect, disconnect, answer, reject };
    }

    setSoftphoneFunctions({
      makeCall: null,
      hangup: stopMonitor,
      toggleMute: toggleMonitorMute,
      connect: null,
      disconnect: null,
      answer: null,
      reject: null
    });

    startMonitor().catch((err) => {
      console.warn('[SUPERVISOR][MONITOR] Failed to start monitor:', err?.message || err);
      cleanup();
      clearMonitoringSession();
    });
    // We intentionally avoid depending on context functions to prevent update loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monitoringSession?.agentId, monitoringSession?.mode]);

  return (
    <div style={{ display: 'none' }}>
      <audio ref={remoteAudioRef} autoPlay />
      <div ref={audioContainerRef} />
    </div>
  );
}
